import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import {
  createStockIssue,
  getClinicStocksWoLoader,
} from "../../../redux/reducers/clinicSlice";
import { addIcon, delBtn } from "../../../util/CommonIconStyle";
import { requestTypeList } from "../../../util/dropList";
import { requestAndTransferEnum } from "../../../util/enum";
import { patchMandateSession } from "../../../util/mandateSessionStorage";

const initVal = {
  requestType: requestTypeList[1],
  requestTypeError: false,
  location: null,
  locationErr: false,
  requestedBy: null,
  requestedDate: new Date(),
  requestedByErr: false,
};
const reqTxt = "This field is required";
const initObj = {
  itemName: null,
  itemNameErr: false,
  name: null,
  quantity: null,
  nameErr: false,
  qtyErr: false,
  issQtyErr: false,
  issueQuantity: null,
  fromLocation: null,
  fromLocationErr: false,
  toLocation: null,
  toLocationErr: false,
  batchNumber: null,
  batchErr: false,
};
const requestAllowedCategories = [
  "medication",
  "diet",
  "nutrition and supplements",
  "fluid therapy",
  "other parasite treatment",
  "deworming",
  "flea & tick treatment",
];
const defObj = {
  locations: [],
  clinic: {},
  stocks: [],
  vets: [],
  dataSharing: {},
  clinicBranches: [],
};

const StockRequestModal = ({
  modVsble,
  handleModClose = () => {},
  selectedItem = null,
  issueModVsble = false,
}) => {
  const dispatch = useDispatch();
  const [modValues, setModValues] = useState(initVal);
  const [addedList, setAddedList] = useState([{ ...initObj, i: 0 }]);
  const { stockIssues, clinicRoles } = useSelector((state) => state?.clinic);
  // const vets = useSelector((state) => state?.vet?.vets);
  const role = localStorage.getItem("role");
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    locations,
    clinic: clinicDet,
    stocks: clinicStocks,
    vets,
    dataSharing,
    clinicBranches,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        clinic: reqData?.clinic ?? {},
        dataSharing: reqData?.dataSharing ?? {},
        clinicBranches: Array.isArray(reqData?.clinicBranches)
          ? reqData?.clinicBranches
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);
  const rolesHaveAccess = ["ROLE_CLINIC", "ROLE_INVENTORY"].includes(role);

  useEffect(() => {
    dispatch(
      getClinicStocksWoLoader(`?inventryData=${dataSharing?.inventryData}`),
    ).then((stcRes) => {
      if (stcRes?.payload) {
        patchMandateSession({
          stocks: stcRes?.payload?.data ?? [],
        });
      }
    });
  }, []);

  const getLocationsByRequestType = (typ = null) => {
    if (locations?.length === 0 || !locations) return [];
    const reqTypes =
      typ === "Consumption" ? ["consumption"] : ["warehouse", "storage"];
    return locations
      ?.filter((li) => {
        const typMatc = reqTypes?.includes(li?.locationType?.toLowerCase());
        const cliniLoc = ["Consumption", "Internal Transfer"].includes(
          modValues?.requestType?.value,
        )
          ? li?.createdBy === clinicDet?.id
          : li?.createdBy === modValues?.requestedBy?.value;
        return typMatc && cliniLoc;
      })
      ?.map((loc) => ({
        name: loc?.locationName,
        value: loc?.locationName,
        id: loc?.id,
      }));
  };

  const handleChange = (name, value) => {
    const reqObj = { ...modValues, [name]: value };
    if (name === "location") {
      reqObj.locationErr = value ? false : true;
    }
    if (name === "requestType") {
      reqObj.requestTypeError = value ? false : true;
      reqObj.location = null;
      reqObj.requestedBy = null;
      setAddedList([{ ...initObj, i: 0 }]);
    }
    if (name === "requestedBy") {
      reqObj.requestedByErr = value ? false : true;
    }
    setModValues(reqObj);
  };

  const getPresList = () => {
    if (
      !clinicStocks ||
      clinicStocks?.length === 0 ||
      !modValues?.requestType?.value ||
      (modValues?.requestType?.value !== "External Transfer" &&
        !modValues?.location?.id)
    )
      return [];
    const reqList = clinicStocks?.filter((dta) => dta?.active === "Y");
    return reqList?.map((li) => ({
      name: li?.tradeName,
      value: li?.tradeName,
      stockId: li?.stockId,
      type: li?.category,
      productName: li?.productName,
    }));
  };

  const getVetList = () => {
    const reqList = vets?.map((li) => ({
      name: li?.name,
      value: li?.doctorId,
      department: li?.department,
    }));
    return reqList;
  };

  const getBranchList = () => {
    const reqList = clinicBranches
      ?.filter((li) => li?.id !== clinicDet?.id)
      ?.map((li) => ({
        name: li?.branchName ?? "",
        value: li?.id ?? null,
      }));
    return reqList;
  };

  const getBatchList = (stockId = null) => {
    if (!stockId) return [];
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
    const clinicBatches = reqStock?.stockBatch?.filter((bt) => {
      const isSameClinic = bt?.createdBy
        ? bt?.createdBy === clinicDet?.id
        : true;
      const hasQty = bt?.quantity > 0;
      return isSameClinic && hasQty;
    });
    return clinicBatches?.map((bat) => {
      const reqLocation = locations?.find((loc) => loc?.id === bat?.locationId);
      return {
        name: bat?.batchNo,
        value: bat?.batchNo,
        id: bat?.batchId,
        subunits: bat?.subunits,
        quantity: bat?.quantity,
        locationId: bat?.locationId,
        locationName: reqLocation?.locationName ?? "",
      };
    });
  };

  const onChange = (name, value, i, stockId) => {
    const reqList = [...addedList];
    const reqObj = {
      ...reqList[i],
      [name]: value,
      itemNameErr:
        name === "itemName"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.itemNameErr,
      nameErr:
        name === "name"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.nameErr,
      qtyErr:
        name === "quantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 0
          : reqList[i]?.qtyErr,
      issQtyErr:
        name === "issueQuantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 1
          : reqList[i]?.issQtyErr,
      batchErr:
        name === "batchNumber"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.batchErr,
      fromLocationErr:
        name === "fromLocation"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.fromLocationErr,
      toLocationErr:
        name === "toLocation"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.toLocationErr,
    };
    if (name === "name" && stockId) {
      reqObj.stockId = stockId;
      reqObj.batchNumber = getBatchList(stockId)?.[0] ?? null;
    }
    reqList[i] = reqObj;
    setAddedList(reqList);
  };

  const handleAddNew = () => {
    setAddedList([...addedList, { ...initObj, i: addedList?.length }]);
  };

  const getBatchById = (stckId = null, batId = null) => {
    if (!stckId || !batId) return "-";
    const batchList = clinicStocks?.find(
      (stc) => stc?.stockId === stckId,
    )?.stockBatch;
    return batchList?.find((li) => li?.batchId === batId) ?? null;
  };

  const handleDeleteItem = (i) => {
    const reqList = addedList
      ?.filter((li) => li?.i !== i)
      ?.map((li, i) => ({ ...li, i }));
    setAddedList(reqList);
  };

  const getStockBasedQtyWithToLocation = (stckId = null) => {
    if (
      !stckId ||
      (issueModVsble ? !selectedItem?.locationId : !modValues?.location?.id)
    )
      return "-";
    const batchList = clinicStocks?.find(
      (stc) => stc?.stockId === stckId,
    )?.stockBatch;
    const reqQty =
      batchList
        ?.filter(
          (li) =>
            li?.locationId ===
            (issueModVsble
              ? selectedItem?.locationId
              : modValues?.location?.id),
        )
        ?.reduce((sum, item) => sum + (item?.quantity || 0), 0) ?? "-";
    return reqQty;
  };

  const handleValidate = () => {
    const reqList = addedList?.map((li) => {
      const reqObj = {
        ...li,
        nameErr: li?.name === "" || li?.name?.length === 0 || !li?.name,
        qtyErr:
          li?.quantity === "" ||
          li?.quantity?.length === 0 ||
          !li?.quantity ||
          Number(li?.quantity) < 0,
        batchErr: !li?.batchNumber?.id,
      };
      return reqObj;
    });
    setAddedList(reqList);
    return reqList?.some((li) => li?.nameErr || li?.qtyErr || li?.batchErr);
  };

  const handleSave = () => {
    if (
      !modValues?.requestType?.value ||
      (modValues?.requestType?.value !== "External Transfer" &&
        !modValues?.location?.id) ||
      (rolesHaveAccess ? !modValues?.requestedBy?.value : false)
    ) {
      setModValues({
        ...modValues,
        requestTypeError: !modValues?.requestType?.value,
        locationErr:
          modValues?.requestType?.value !== "External Transfer" &&
          !modValues?.location?.id,
        requestedByErr: rolesHaveAccess
          ? !modValues?.requestedBy?.value
          : false,
      });
      return;
    }
    if (handleValidate() || addedList?.length === 0) return;
    const stockIssueDetails = addedList?.map((li) => {
      const reqStock = clinicStocks?.find(
        (stc) => stc?.stockId === li?.stockId,
      );
      const clinicBatches = reqStock.stockBatch?.filter((bt) => {
        const isSameClinic = bt?.createdBy
          ? bt?.createdBy === clinicDet?.id
          : true;
        const hasQty = bt?.quantity > 0;
        return isSameClinic && hasQty;
      });
      const reqBatch = li?.batchNumber?.id
        ? clinicBatches?.find((bat) => bat?.batchId === li?.batchNumber?.id)
        : clinicBatches?.[0];
      const reqObj = {
        itemName: li?.name ?? "",
        quantity: Number(li?.quantity ?? 1),
        status: rolesHaveAccess
          ? li?.stockId
            ? "Completed"
            : "Pending"
          : "Pending",
        stockId: li?.stockId ?? null,
        batchId: clinicBatches?.length > 0 ? (reqBatch?.batchId ?? null) : null,
        mode: "manual",
        isRejected: false,
        toLocationName:
          modValues?.requestType?.value === "External Transfer"
            ? null
            : (modValues?.location?.name ?? null),
        toLocationId:
          modValues?.requestType?.value === "External Transfer"
            ? null
            : (modValues?.location?.id ?? null),
      };
      if (rolesHaveAccess) {
        const reqBatch = getBatchById(li?.stockId, li?.batchNumber?.id);
        reqObj.issueQuantity = Number(li?.quantity ?? 1);
        reqObj.fromLocationName = reqBatch?.location ?? null;
        reqObj.fromLocationId = reqBatch?.locationId ?? null;
        if (!reqBatch?.location) {
          const reqLoc = locations?.find(
            (li) => li?.id === reqBatch?.locationId,
          );
          if (reqLoc?.locationName) {
            reqObj.fromLocationName = reqLoc?.locationName ?? null;
          }
        }
      }
      return reqObj;
    });
    const reqObj = {
      clinicId:
        modValues?.requestType?.value === "External Transfer"
          ? modValues?.requestedBy?.value
          : clinicDet?.id,
      stockIssueDetails,
      locationId:
        modValues?.requestType?.value === "External Transfer"
          ? null
          : modValues?.location?.id,
      requestType: requestAndTransferEnum?.[modValues?.requestType?.value],
      requestedDate: moment(new Date(modValues?.requestedDate)).format(
        "YYYY-MM-DD",
      ),
      requestedBy: modValues?.requestedBy?.value ?? profile?.id ?? null,
    };
    dispatch(createStockIssue(reqObj)).then((res) => {
      if (res?.payload) {
        handleModClose("apiSuccess");
      }
    });
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => handleModClose("close")}
        header="Create"
        modalWidth={70}
        modal
      >
        <div className="scroll-60vh">
          <div className="flex-row">
            <div className="w23Per">
              <Typography className="font-bold blue-color fs12 mb5">
                Request Type <span className="red-color">*</span>
              </Typography>
              <CustomSelect
                list={requestTypeList}
                value={modValues?.requestType}
                error={modValues?.requestTypeError}
                helperText={
                  modValues?.requestTypeError ? "This is required field" : ""
                }
                select
                handleChange={(e, value) => {
                  handleChange("requestType", value);
                }}
                search
              />
            </div>
            {rolesHaveAccess ? (
              <div className="w23Per ml15">
                <Typography className="font-bold blue-color fs12 mb5">
                  Requested By <span className="red-color">*</span>
                </Typography>
                <CustomSelect
                  list={
                    modValues?.requestType?.value === "External Transfer"
                      ? getBranchList()
                      : getVetList()
                  }
                  value={modValues?.requestedBy}
                  error={modValues?.requestedByErr}
                  helperText={
                    modValues?.requestedByErr ? "This is required field" : ""
                  }
                  select
                  handleChange={(e, value) => {
                    handleChange("requestedBy", value);
                  }}
                  search
                />
              </div>
            ) : null}
            {modValues?.requestType?.value !== "External Transfer" ? (
              <div className="w23Per ml15">
                <Typography className="font-bold blue-color fs12 mb5">
                  To Location <span className="red-color">*</span>
                </Typography>
                <CustomSelect
                  list={getLocationsByRequestType(
                    modValues?.requestType?.value ?? null,
                  )}
                  value={modValues?.location}
                  error={modValues?.locationErr}
                  helperText={
                    modValues?.locationErr ? "This is required field" : ""
                  }
                  select
                  handleChange={(e, value) => {
                    handleChange("location", value);
                  }}
                  search
                />
              </div>
            ) : null}
            {rolesHaveAccess ? (
              <>
                <div className="w23Per ml15">
                  <Typography className="font-bold blue-color fs12 mb5">
                    Request Date <span className="red-color">*</span>
                  </Typography>
                  <CustomTextField
                    value={modValues?.requestedDate}
                    handleChange={(e) => {
                      handleChange("requestedDate", e?.target?.value);
                    }}
                    mobileDate
                    fullWidth
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="flex-row-ali-cen ph20 mt20">
            <div className={rolesHaveAccess ? "w30Per" : "w70Per"}>
              <Typography className="font-bold blue-color fs12">
                Product Name <span className="red-color">*</span>
              </Typography>
            </div>
            {rolesHaveAccess ? (
              <div className="w20Per">
                <Typography className="font-bold blue-color fs12">
                  Batch No. <span className="red-color">*</span>
                </Typography>
              </div>
            ) : null}
            {rolesHaveAccess ? (
              <>
                <div className="w15Per">
                  <Typography className="font-bold blue-color fs12">
                    Available Qty @ Location
                  </Typography>
                </div>
                <div className="w12Per">
                  <Typography className="font-bold blue-color fs12">
                    From Location
                  </Typography>
                </div>
              </>
            ) : null}
            <div className={rolesHaveAccess ? "w18Per" : "w25Per"}>
              <Typography className="font-bold blue-color fs12">
                {rolesHaveAccess ? "Issuing" : "Requesting"} Qty
                <span className="red-color">*</span>
              </Typography>
            </div>
            <div className="w5Per" />
          </div>
          <div className="blue-hor-split mt5" />
          {addedList?.map((al, i) => {
            return (
              <div
                className="flex-row ph20 mt10 plan-bor"
                key={`iss_${al?.i ?? i}`}
              >
                <div
                  className={`${rolesHaveAccess ? "w30Per" : "w70Per"} pr10`}
                >
                  <SearchableSelect
                    variant="procedure"
                    list={getPresList()}
                    value={al?.name}
                    error={al?.nameErr}
                    helperText={al?.nameErr ? reqTxt : ""}
                    plusBtn
                    mandatoryField
                    isForProcedure={false}
                    showQtyNearExpiry={false}
                    handleChange={(e, value) => {
                      onChange(
                        "name",
                        value?.name ?? value?.value ?? "",
                        al?.i,
                        value?.stockId ?? null,
                      );
                    }}
                    maxFilteredOptions={100}
                  />
                </div>
                {rolesHaveAccess ? (
                  <div className="w20Per pr10">
                    <CustomSelect
                      list={getBatchList(al?.stockId)}
                      value={al?.batchNumber}
                      error={al?.batchErr}
                      helperText={al?.batchErr ? reqTxt : ""}
                      search
                      handleChange={(e, value) => {
                        onChange("batchNumber", value, al?.i);
                      }}
                      isBatchNumber
                    />
                  </div>
                ) : null}
                {rolesHaveAccess ? (
                  <>
                    <div className="w15Per">
                      <Typography className="align-center-wh ml3 black22 fs14 txt-regular h40">
                        {al?.batchNumber?.quantity ?? "-"}
                        {/* {getStockBasedQtyWithToLocation(al?.stockId)} */}
                      </Typography>
                    </div>
                    <div className="w12Per pr10">
                      <Typography className="align-center-wh ml3 black22 fs14 txt-regular h40">
                        {al?.batchNumber?.locationName ?? "-"}
                        {/* {getBatchById(al?.stockId, al?.batchNumber?.id)
                          ?.location ?? "-"} */}
                      </Typography>
                    </div>
                  </>
                ) : null}
                <div className={rolesHaveAccess ? "w18Per pr10" : "w25Per"}>
                  <CustomTextField
                    value={al?.quantity}
                    error={al?.qtyErr}
                    helperText={al?.qtyErr ? reqTxt : ""}
                    handleChange={(e) => {
                      onChange("quantity", e?.target?.value, al?.i);
                    }}
                    type="number"
                    fullWidth
                  />
                </div>
                <div className="w5Per">
                  <div
                    className="cursor mt5"
                    onClick={() => handleDeleteItem(al?.i)}
                  >
                    <div className="flex-center">
                      <DeleteOutlineIcon sx={delBtn} />
                    </div>
                    <Typography className="red3 fs8 txt-semi-bold text-align-center">
                      Delete
                    </Typography>
                  </div>
                </div>
              </div>
            );
          })}
          {/* <div className="flex-row-ali-cen mt5">
            <Typography className="txt-semi-bold fs14 black">Note: </Typography>
            <Typography className="font-medium fs14 ml5 gray3">
              Please note that all quantities are in subunits.
            </Typography>
          </div> */}
          {modValues?.location?.id ||
          modValues?.requestType?.value === "External Transfer" ? (
            <div
              className="flex-row-ali-cen mt20 cursor"
              onClick={handleAddNew}
            >
              <div className="flex-center plus-icon">
                <AddIcon sx={addIcon} />
              </div>
              <Typography className="blue-color fs16 ml5 font-bold">
                Add
              </Typography>
            </div>
          ) : null}
          {addedList?.length > 0 ? (
            <div className="flex1-center mv10">
              <div>
                <CustomButton text="Submit" smallBtn onClick={handleSave} />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>
    </>
  );
};

export default StockRequestModal;
